(() => {
  if (window.LuciaMaterialPage) { window.LuciaMaterialPage.refresh(); return; }
  let chapters = [], navigation = null, frame = 0, current = '';
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const headers = [...document.querySelectorAll('sticky-header,#sticky-header,#page-header,.section-header,header[role="banner"],.header-wrapper')];
  const pinnedHeader = document.querySelector('.section-header:has(#page-header)');
  const root = document.documentElement;
  const headerHeight = () => pinnedHeader ? pinnedHeader.offsetHeight : headers.reduce((height, element) => {
    const box = element.getBoundingClientRect();
    return box.top <= 2 && box.bottom > 0 && box.bottom < innerHeight * .4 ? Math.max(height, box.bottom) : height;
  }, 0);
  const menuOpen = () => headers.some(header => [...header.querySelectorAll('.bls__menu-parent > .submenu,.mega-menu__content,.header__submenu')].some(panel => {
    const box = panel.getBoundingClientRect(), style = getComputedStyle(panel);
    return box.width > 120 && box.height > 70 && box.right > 0 && box.left < innerWidth && box.bottom > 0 && box.top < innerHeight && style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) > .05;
  }));
  const offset = () => Math.round(headerHeight() + (navigation?.offsetHeight || 0));
  function update() {
    frame = 0;
    const top = offset(), header = Math.round(headerHeight());
    root.style.setProperty('--lmc-header-height', `${header}px`);
    root.style.setProperty('--lmc-offset', `${top}px`);
    if (!navigation || !chapters.length) return;
    navigation.classList.toggle('is-menu-covered', menuOpen());
    const active = chapters.find(chapter => {
      const box = chapter.getBoundingClientRect();
      return box.top <= top + 80 && box.bottom > top + 80;
    }) || chapters.find(chapter => chapter.getBoundingClientRect().bottom > top) || chapters.at(-1);
    if (active.id === current) return;
    current = active.id;
    navigation.querySelectorAll('.lmn__links a').forEach(link => {
      if (link.hash === `#${current}`) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
    navigation.querySelector('select').value = current;
    const selected = navigation.querySelector('[aria-current]');
    const rail = navigation.querySelector('.lmn__links');
    if (selected && rail.clientWidth && rail.scrollWidth > rail.clientWidth) {
      const x = selected.offsetLeft - rail.clientWidth / 2 + selected.offsetWidth / 2;
      rail.scrollTo({left:x, behavior:reduced() ? 'instant' : 'smooth'});
    }
  }
  function queue() { if (!frame) frame = requestAnimationFrame(update); }
  function jump(id) {
    const target = document.getElementById(id);
    if (!target?.matches('lucia-material-chapter')) return;
    history.replaceState(null, '', `#${id}`);
    window.scrollTo({top:scrollY + target.getBoundingClientRect().top - offset(), behavior:reduced() ? 'instant' : 'smooth'});
  }
  function fit() {
    for (const chapter of chapters) {
      chapter.classList.remove('lmc--expanded');
      if (innerWidth < 1000 || innerHeight < 681) continue;
      const heading = chapter.querySelector('.lmc__header');
      const longHeading = [...heading.children].some(child => child.scrollHeight > 101);
      const longProducts = [...chapter.querySelectorAll('.lmc__product-copy')].some(copy => copy.scrollHeight > 105);
      chapter.classList.toggle('lmc--expanded', longHeading || longProducts);
    }
  }
  function refresh() {
    chapters = [...document.querySelectorAll('lucia-material-chapter')];
    navigation = document.querySelector('lucia-material-navigation');
    chapters.forEach((chapter, index) => {
      chapter.querySelector('[data-material-number]').textContent = String(index + 1).padStart(2, '0');
    });
    if (navigation) {
      const rail = navigation.querySelector('.lmn__links'), select = navigation.querySelector('select');
      rail.replaceChildren(); select.replaceChildren();
      chapters.forEach((chapter, index) => {
        const title = chapter.dataset.navLabel, number = String(index + 1).padStart(2, '0');
        const link = document.createElement('a'), small = document.createElement('small'), label = document.createElement('span');
        link.href = `#${chapter.id}`; small.textContent = number; label.textContent = title;
        small.setAttribute('aria-hidden', 'true'); link.append(small, label); rail.append(link);
        const option = document.createElement('option'); option.value = chapter.id; option.textContent = `${number} ${title}`; select.append(option);
      });
      navigation.hidden = chapters.length === 0;
    }
    current = ''; update(); fit();
  }
  class Chapter extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.timers = new Set();
      const signal = this.controller.signal;
      const delay = (fn, time) => { const id = setTimeout(() => { this.timers.delete(id); fn(); }, time); this.timers.add(id); return id; };
      this.querySelectorAll('.lmc__product-image-link').forEach(link => {
        const scene = link.querySelector('.lmc__scene');
        if (!scene) return;
        const figure = link.querySelector('figure');
        let ready = false, pending, hovered = false, press = null, hoverTimer, touchTimer, suppress = false;
        const paint = () => figure.classList.toggle('is-scene-visible', ready && (hovered || press?.held || link.matches(':focus-visible')));
        const load = () => pending ||= (async () => {
          scene.src = scene.dataset.src;
          try { await scene.decode(); ready = scene.naturalWidth > 0; } catch { ready = false; }
          if (!signal.aborted) paint();
        })();
        const endTouch = () => { clearTimeout(touchTimer); press = null; paint(); };
        const listen = (event, fn) => link.addEventListener(event, fn, {signal});
        listen('pointerenter', event => {
          if (event.pointerType === 'touch' || !matchMedia('(hover:hover)').matches) return;
          load(); clearTimeout(hoverTimer); hoverTimer = delay(() => { hovered = true; paint(); }, 80);
        });
        listen('pointerleave', () => { clearTimeout(hoverTimer); hovered = false; endTouch(); paint(); });
        listen('pointerdown', event => {
          suppress = false;
          if (event.pointerType !== 'touch' || !event.isPrimary) return;
          endTouch(); press = {id:event.pointerId, x:event.clientX, y:event.clientY, held:false}; load();
          touchTimer = delay(() => { if (press) { press.held = true; suppress = true; paint(); } }, 300);
        });
        listen('pointermove', event => { if (press && Math.hypot(event.clientX-press.x,event.clientY-press.y)>12) endTouch(); });
        listen('pointerup', endTouch); listen('pointercancel', endTouch); listen('lostpointercapture', endTouch);
        listen('contextmenu', event => { if (press || suppress) event.preventDefault(); });
        listen('click', event => { if (suppress && event.detail > 0) { event.preventDefault(); suppress = false; } });
        listen('focus', () => { if (link.matches(':focus-visible')) load(); paint(); });
        listen('blur', paint);
      });
      requestAnimationFrame(refresh);
    }
    disconnectedCallback() { this.controller?.abort(); this.timers?.forEach(clearTimeout); requestAnimationFrame(refresh); }
  }
  class Navigation extends HTMLElement {
    connectedCallback() {
      this.controller?.abort(); this.controller = new AbortController();
      const signal = this.controller.signal;
      this.addEventListener('click', event => {
        const link = event.target.closest('.lmn__links a');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); jump(link.hash.slice(1));
      }, {signal});
      this.querySelector('select').addEventListener('change', event => jump(event.target.value), {signal});
      requestAnimationFrame(refresh);
    }
    disconnectedCallback() { this.controller?.abort(); requestAnimationFrame(refresh); }
  }
  window.LuciaMaterialPage = {refresh};
  customElements.define('lucia-material-chapter', Chapter);
  customElements.define('lucia-material-navigation', Navigation);
  window.addEventListener('scroll', queue, {passive:true});
  window.addEventListener('resize', () => { queue(); fit(); }, {passive:true});
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:reorder', refresh);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(refresh));
  document.addEventListener('shopify:section:select', event => {
    const chapter = event.target.querySelector('lucia-material-chapter');
    if (chapter) jump(chapter.id);
  });
  document.addEventListener('shopify:block:select', event => {
    const chapter = event.target.closest('lucia-material-chapter');
    if (chapter) jump(chapter.id);
  });
  const headerObserver = new MutationObserver(queue);
  const resizeObserver = new ResizeObserver(queue);
  headers.forEach(header => {
    headerObserver.observe(header, {attributes:true,subtree:true,attributeFilter:['class','style','open','aria-expanded']});
    resizeObserver.observe(header);
    header.addEventListener('pointerover', queue, {passive:true});
    header.addEventListener('pointerout', queue, {passive:true});
    header.addEventListener('transitionend', queue, {passive:true});
  });
  document.fonts.ready.then(() => { refresh(); });
  refresh();
})();
